<script setup lang="ts">
import { onMounted, ref } from 'vue';

// Fase 3.2 walkthrough CEO 2026-05-07: cookie consent.
// Persiste decisión en localStorage 'wiservet:cookie-consent' = 'all' | 'essential' | 'declined'.

const STORAGE_KEY = 'wiservet:cookie-consent';
const visible = ref(false);

onMounted(() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) visible.value = true;
  } catch {
    // localStorage disabled — mostramos banner igual.
    visible.value = true;
  }
});

function setConsent(level: 'all' | 'essential') {
  try {
    localStorage.setItem(STORAGE_KEY, level);
  } catch {
    // noop
  }
  visible.value = false;
}
</script>

<template>
  <Transition name="cookie-fade">
    <div v-if="visible" class="cookie-banner" role="dialog" aria-labelledby="cookie-banner-title">
      <div class="cookie-banner-text">
        <h3 id="cookie-banner-title">Cookies en WiserVet</h3>
        <p>
          Usamos cookies esenciales para mantener tu sesión y cookies de funcionalidad para recordar tus preferencias.
          Las cookies analíticas son opcionales y requieren tu consentimiento.
          <a href="/cookies" target="_blank" rel="noopener">Más detalles</a>.
        </p>
      </div>
      <div class="cookie-banner-actions">
        <button class="cookie-btn" @click="setConsent('essential')">Solo esenciales</button>
        <button class="cookie-btn primary" @click="setConsent('all')">Aceptar todas</button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.cookie-banner {
  position: fixed;
  /* 52px (no 16px) para no superponerse con FeedbackBar (footer global fijo
     de /app, ~40px de alto, ver AppShell.vue) -- hallazgo de cto-reviewer
     en PR #94, 2026-07-09. CookieBanner se monta en App.vue (root), así que
     SÍ puede coincidir con /app, a diferencia de WhatsAppFab (solo landing
     pública, nunca se solapa). */
  bottom: 52px;
  left: 16px;
  right: 16px;
  max-width: 640px;
  margin: 0 auto;
  padding: 18px 22px;
  background: var(--bg-surface, #fff);
  border: 1px solid var(--border-subtle, #e4e4e7);
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(20, 24, 30, 0.16);
  z-index: 80;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

@media (min-width: 768px) {
  .cookie-banner {
    flex-direction: row;
    align-items: center;
    gap: 24px;
  }
}

.cookie-banner-text { flex: 1; }
.cookie-banner h3 {
  font-size: 14px;
  margin: 0 0 4px;
  color: var(--fg-strong);
}
.cookie-banner p {
  font-size: 13px;
  margin: 0;
  color: var(--fg-muted);
  line-height: 1.5;
}
.cookie-banner a {
  color: var(--sky-700);
  text-decoration: none;
  font-weight: 500;
}
.cookie-banner a:hover { text-decoration: underline; }
.cookie-banner-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.cookie-btn {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid var(--border-default, #d4d4d8);
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}
.cookie-btn:hover {
  border-color: var(--sky-500);
  color: var(--sky-700);
}
.cookie-btn.primary {
  background: var(--sky-600);
  border-color: var(--sky-600);
  color: #fff;
}
.cookie-btn.primary:hover {
  background: var(--sky-700);
  border-color: var(--sky-700);
}

.cookie-fade-enter-active, .cookie-fade-leave-active {
  transition: all 0.3s ease;
}
.cookie-fade-enter-from, .cookie-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
